import { api } from './api';

class LeadGenerationService {
  // Search Areas endpoints
  async getSearchAreas(limit = 50) {
    try {
      const response = await api.get(`/api/leads/search-areas/?limit=${limit}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async createSearchArea(searchAreaData) {
    try {
      const response = await api.post('/api/leads/search-areas/', searchAreaData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async updateSearchArea(areaId, updateData) {
    try {
      const response = await api.patch(`/api/leads/search-areas/${areaId}/`, updateData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async deleteSearchAreas(areaIds) {
    try {
      const response = await api.delete('/api/leads/search-areas/', {
        data: { area_ids: areaIds }
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async deleteSearchArea(areaId) {
    try {
      const response = await api.delete('/api/leads/search-areas/', {
        data: { area_ids: [areaId] }  // Wrap single ID in array
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Places Search endpoint
  async searchPlaces(searchData) {
    try {
      const response = await api.post('/api/leads/search/', searchData);
      return { data: response.data, error: null };
    } catch (error) {
      console.error('Search API Error:', error.response?.data);
      console.error('Search request data:', searchData);
      return { data: null, error: error.response?.data || error.message };
    }
  }
  
  // Save places after user finalization
  async savePlaces(placesData, searchSessionId = null) {
    try {
      const response = await api.post('/api/leads/save-places/', {
        places: placesData,
        search_session_id: searchSessionId
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // User Places endpoints
  async getUserPlaces(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/api/leads/places/?${queryParams}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async deletePlaces(placeIds) {
    try {
      const response = await api.delete('/api/leads/places/', {
        data: { place_ids: placeIds }
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Search Sessions endpoints
  async getSearchSessions(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/api/leads/sessions/?${queryParams}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Leads endpoints
  async getLeads(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/api/leads/leads/?${queryParams}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async createLead(leadData) {
    try {
      const response = await api.post('/api/leads/leads/', leadData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async updateLead(leadId, updateData) {
    try {
      const response = await api.patch(`/api/leads/leads/${leadId}/`, updateData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async deleteLead(leadId) {
    try {
      const response = await api.delete(`/api/leads/leads/${leadId}/`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Statistics endpoint
  async getSearchStats() {
    try {
      const response = await api.get('/api/leads/stats/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await api.get('/api/leads/health/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Place exclusion management
  async getExclusions() {
    try {
      const response = await api.get('/api/leads/exclusions/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }
  
  async addExclusionPatterns(patterns) {
    try {
      const response = await api.post('/api/leads/exclusions/', { patterns });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }
  
  async removeExclusionPatterns(exclusionIds) {
    try {
      const response = await api.delete('/api/leads/exclusions/', {
        data: { exclusion_ids: exclusionIds }
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }
  
  // Place cleanup
  async cleanupPlaces(placeIds, addExclusionPatterns = []) {
    try {
      const response = await api.post('/api/leads/cleanup/', {
        place_ids: placeIds,
        add_exclusion_patterns: addExclusionPatterns
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async exportPlaces() {
  try {
    const response = await api.get('/api/leads/places/export/', {
      responseType: 'blob'
    });
    
    // Create blob and download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers or create default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'saved_places.xlsx';  // Changed to .xlsx
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { data: { success: true, filename }, error: null };
  } catch (error) {
    return { data: null, error: error.response?.data || error.message };
  }
}

async exportLeads() {
  try {
    const response = await api.get('/api/leads/leads/export/', {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'leads.xlsx';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { data: { success: true, filename }, error: null };
  } catch (error) {
    return { data: null, error: error.response?.data || error.message };
  }
}

  // Polygon circle generation - Updated to support tube shapes
  async generatePolygonCircles(polygonCoordinates, radius, shapeType = 'polygon') {
    try {
      let requestData = {
        radius: radius,
        shape_type: shapeType
      };

      if (shapeType === 'polygon') {
        requestData.polygon_coordinates = polygonCoordinates;
      } else if (shapeType === 'tube') {
        requestData.outer_polygon_coordinates = polygonCoordinates.outer;
        requestData.inner_polygon_coordinates = polygonCoordinates.inner;
      }

      const response = await api.post('/api/leads/generate-polygon-circles/', requestData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Tube shape generation - Convenience method
  async generateTubeCircles(outerCoordinates, innerCoordinates, radius) {
    return this.generatePolygonCircles(
      { outer: outerCoordinates, inner: innerCoordinates }, 
      radius, 
      'tube'
    );
  }

  async getSearchShapes(limit = 100) {
  try {
    const response = await api.get(`/api/leads/search-shapes/?limit=${limit}`);
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: error.response?.data || error.message };
  }
}

  async createMultipleLeads(placeIds) {
  try {
    const response = await api.post('/api/leads/leads/bulk/', {
      place_ids: placeIds
    });
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error: error.response?.data || error.message };
  }
}

  // Utility methods for shape calculations
  static createPentagonPoints(center, radius) {
    const lat = center.lat;
    const lng = center.lng;
    const points = [];
    
    for (let i = 0; i < 5; i++) {
      const angle = (i * 72 - 90) * (Math.PI / 180);
      const pointLat = lat + radius * Math.cos(angle);
      const pointLng = lng + radius * Math.sin(angle) / Math.cos(lat * Math.PI / 180);
      points.push({ lat: pointLat, lng: pointLng });
    }
    
    return points;
  }

  static degreesToMeters(degrees) {
    return Math.round(degrees * 111000);
  }

  static metersToKilometers(meters) {
    return meters >= 1000 ? `${(meters / 1000).toFixed(1)}km` : `${meters}m`;
  }

  // Format radius for display
  static formatRadius(radius) {
    if (radius >= 1000) {
      return `${(radius / 1000).toFixed(1)}km`;
    }
    return `${radius}m`;
  }

  // Calculate distance between two points in meters
  static calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  // Validate polygon coordinates
  static validatePolygonCoordinates(coordinates) {
    if (!Array.isArray(coordinates) || coordinates.length < 3) {
      return { valid: false, error: 'Polygon must have at least 3 points' };
    }

    for (let i = 0; i < coordinates.length; i++) {
      const coord = coordinates[i];
      if (!coord || typeof coord.latitude !== 'number' || typeof coord.longitude !== 'number') {
        return { valid: false, error: `Invalid coordinate at index ${i}` };
      }
      
      if (Math.abs(coord.latitude) > 90 || Math.abs(coord.longitude) > 180) {
        return { valid: false, error: `Coordinate out of range at index ${i}` };
      }
    }

    return { valid: true };
  }

  // Calculate polygon area (approximate, for small polygons)
  static calculatePolygonArea(coordinates) {
    if (coordinates.length < 3) return 0;

    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += coordinates[i].latitude * coordinates[j].longitude;
      area -= coordinates[j].latitude * coordinates[i].longitude;
    }

    area = Math.abs(area) / 2;
    
    // Convert to square meters (very approximate for small areas)
    const metersPerDegree = 111000;
    return area * metersPerDegree * metersPerDegree;
  }

  // Check if point is inside polygon
  static isPointInPolygon(point, polygon) {
    const x = point.longitude;
    const y = point.latitude;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].longitude;
      const yi = polygon[i].latitude;
      const xj = polygon[j].longitude;
      const yj = polygon[j].latitude;

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }

  // Generate grid of circles within polygon bounds
  static generateCircleGrid(polygon, radius, spacing = null) {
    if (!spacing) {
      spacing = radius * 1.5; // Default spacing
    }

    // Find bounding box
    const lats = polygon.map(p => p.latitude);
    const lngs = polygon.map(p => p.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const circles = [];
    const spacingDegrees = spacing / 111000; // Convert meters to degrees (approximate)

    for (let lat = minLat; lat <= maxLat; lat += spacingDegrees) {
      for (let lng = minLng; lng <= maxLng; lng += spacingDegrees) {
        const point = { latitude: lat, longitude: lng };
        
        if (this.isPointInPolygon(point, polygon)) {
          circles.push({
            center_point: point,
            radius: radius,
            latitude: lat,
            longitude: lng
          });
        }
      }
    }

    return circles;
  }

  // Optimize circle placement using density data (placeholder)
  static optimizeCirclePlacement(circles, densityData = null) {
    // This would integrate with AI optimization
    // For now, just return the circles with confidence scores
    return circles.map((circle, index) => ({
      ...circle,
      id: `circle_${index}`,
      confidence_score: Math.random() * 0.5 + 0.5, // Random score between 0.5-1
      predicted_density: Math.random() * 10 + 1, // Random density 1-11
      grid_x: Math.floor(index / 10),
      grid_y: index % 10
    }));
  }
}

export const leadGenService = new LeadGenerationService();
export default LeadGenerationService;